import { useCallback, useEffect, useRef } from "react";

import type { CarouselApi } from "@/components/ui/carousel";

const TRANSITION_DURATION_MS = 200;

export const useCarouselAutoHeight = (api: CarouselApi | undefined): void => {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const currentSlideIndexRef = useRef<number>(-1);

  const updateHeight = useCallback((rootNode: HTMLElement, slideNode: HTMLElement) => {
    rootNode.style.height = `${slideNode.offsetHeight}px`;
  }, []);

  const observeActiveSlide = useCallback(
    (rootNode: HTMLElement, slideNodes: HTMLElement[], index: number) => {
      resizeObserverRef.current?.disconnect();

      const activeSlide = slideNodes[index];
      if (!activeSlide) {
        return;
      }

      currentSlideIndexRef.current = index;
      updateHeight(rootNode, activeSlide);

      const observer = new ResizeObserver(() => {
        updateHeight(rootNode, activeSlide);
      });
      observer.observe(activeSlide);
      resizeObserverRef.current = observer;
    },
    [updateHeight],
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    const rootNode = api.rootNode();

    rootNode.style.transition = `height ${TRANSITION_DURATION_MS}ms ease`;

    const initialIndex = api.selectedScrollSnap();
    observeActiveSlide(rootNode, api.slideNodes(), initialIndex);

    const onSelect = () => {
      const selectedIndex = api.selectedScrollSnap();
      if (selectedIndex !== currentSlideIndexRef.current) {
        observeActiveSlide(rootNode, api.slideNodes(), selectedIndex);
      }
    };

    const onReInit = () => {
      observeActiveSlide(rootNode, api.slideNodes(), api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    api.on("reInit", onReInit);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onReInit);
      resizeObserverRef.current?.disconnect();
      rootNode.style.transition = "";
      rootNode.style.height = "";
    };
  }, [api, observeActiveSlide]);
};
